/**
 * Price Board conventions. Stocks show in thousand VND, as every Vietnamese board does (20.45),
 * which is also the unit the price feed already sends; the order API takes raw VND, so the
 * ticket converts at that boundary. Futures are index points throughout.
 */
import { formatPrice } from '../../utils/numberFormat';

export type Tone = 'ceiling' | 'floor' | 'up' | 'down' | 'ref' | 'none';

export const isFutureSymbol = (symbol: string): boolean => /^VN30F\d{4}$/.test(symbol.toUpperCase());

/** Board colour of a price against the reference, the band taking precedence. */
export function toneOf(
    price: number | null | undefined,
    ref: number | null | undefined,
    ceiling?: number | null,
    floor?: number | null,
): Tone {
    if (price == null || !(price > 0)) return 'none';
    const eq = (a: number, b?: number | null) => b != null && b > 0 && Math.abs(a - b) < 1e-6;
    if (eq(price, ceiling)) return 'ceiling';
    if (eq(price, floor)) return 'floor';
    if (ref == null || !(ref > 0) || eq(price, ref)) return 'ref';
    return price > ref ? 'up' : 'down';
}

export const TONE_COLOR: Record<Tone, string> = {
    ceiling: '#C84FF0',
    floor: '#3FC6F0',
    up: '#0ECB81',
    down: '#F6465D',
    ref: '#F0B90B',
    none: '#EAECEF',
};

/** Board price as shown: 2 decimals for a stock (thousand VND), 1 for a future (points). */
export const boardPrice = (symbol: string, v: number | null | undefined): string =>
    formatPrice(v != null && v > 0 ? v : null, isFutureSymbol(symbol) ? 1 : 2);

/** Ticket price (board unit) -> what the order API takes. */
export const toApiPrice = (symbol: string, boardUnit: number): number =>
    isFutureSymbol(symbol) ? boardUnit : Math.round(boardUnit * 1000);

/** Order API price -> board unit. */
export const fromApiPrice = (symbol: string, apiPrice: number): number =>
    isFutureSymbol(symbol) ? apiPrice : apiPrice / 1000;

export const lotOf = (symbol: string): number => (isFutureSymbol(symbol) ? 1 : 100);

/** Price step: futures 0.1 point; stocks by HSX bands in thousand VND. */
export function tickOf(symbol: string, price: number | null | undefined): number {
    if (isFutureSymbol(symbol)) return 0.1;
    const p = price ?? 0;
    if (p < 10) return 0.01;
    if (p < 50) return 0.05;
    return 0.1;
}

const FINAL = ['FILLED', 'CANCELLED', 'REJECTED', 'EXPIRED'];
export const isOpenStatus = (s: string): boolean => !FINAL.includes(s);

export const STATUS_LABEL: Record<string, { label: string; color: string }> = {
    CREATED: { label: 'Created', color: 'default' },
    SENT_TO_BROKER: { label: 'Sent', color: 'processing' },
    RECEIVED_BROKER: { label: 'At broker', color: 'processing' },
    RECEIVED_EXCHANGE: { label: 'Working', color: 'blue' },
    PARTIALLY_FILLED: { label: 'Partly filled', color: 'cyan' },
    PENDING_CANCEL: { label: 'Cancelling', color: 'orange' },
    FILLED: { label: 'Filled', color: 'green' },
    CANCELLED: { label: 'Cancelled', color: 'default' },
    REJECTED: { label: 'Rejected', color: 'red' },
    EXPIRED: { label: 'Expired', color: 'default' },
    UNKNOWN: { label: 'Unknown', color: 'gold' },
};

/** Change against the reference, percent; null until both are known. */
export const pctOf = (last?: number | null, ref?: number | null): number | null =>
    last != null && last > 0 && ref != null && ref > 0 ? ((last - ref) / ref) * 100 : null;

/** Fields a tick can move; each flashes in its own cell. */
export const FLASH_FIELDS = [
    'bid_price_1', 'bid_quantity_1', 'latest_matched_price', 'ask_price_1', 'ask_quantity_1',
    'highest_price', 'lowest_price', 'total_matched_quantity',
] as const;
export type FlashField = (typeof FLASH_FIELDS)[number];

/**
 * Which cells a tick changed, and which way. Only a value that was there before and moved
 * flashes: the first snapshot after load is not "a change".
 */
export function flashesBetween(
    prev: Record<string, Record<string, unknown>>,
    next: Record<string, Record<string, unknown>>,
): Array<{ symbol: string; field: FlashField; dir: 'up' | 'down' }> {
    const out: Array<{ symbol: string; field: FlashField; dir: 'up' | 'down' }> = [];
    for (const [symbol, q] of Object.entries(next)) {
        const before = prev[symbol];
        if (!before || before === q) continue;
        for (const field of FLASH_FIELDS) {
            const a = Number(before[field]);
            const b = Number(q[field]);
            if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0 || a === b) continue;
            out.push({ symbol, field, dir: b > a ? 'up' : 'down' });
        }
    }
    return out;
}
